import React, { useContext, useCallback } from "react";
import { useSortDataList } from "@imminently/react/ui/resource/list";
import {
  Pagination,
  QueryParams,
} from "@imminently/react/store/query";
import { Box } from "@mui/material";
import {
  DataGridPro,
  GridPaginationModel,
  GridRowClassNameParams,
  GridRowSelectionModel,
  GridSortModel,
  GridSortDirection,
} from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "react-use";
import qs from "query-string";
const { stringify } = qs;

import PatientContextMenu, {
  PatientContext,
} from "@/components/PatientList/PatientListContextMenu";
import { PatientPagination } from "./patient-pagination";
import { usePatientList } from "./patient-slice";
import { PatientToolbar } from "./patient-toolbar";
import { useContextMenuValue } from "@/components/ContextMenu";
import {
  usePatientListApiRefCtx,
  WithPatientListApiRefCtx,
  defaultPinnedColumns,
} from "@/components/PatientList/PatientListAPIRefCtx";
import { patientListColumns } from "@/components/PatientList/PatientListColumns";
import { scrollableMixin } from "@/components/scrollable";
import { useScrollLeft } from "@/global";
import { getPatientRoute } from "@/pages";
import { getLocationsByAreas } from "@/resources/locationHelpers";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import type { PatientEncounter } from "@/resources/types";
import { encounterService } from "@/services";
import { css, type Theme } from "@/theme";
import { get, set } from "lodash";
import { WORK_OFFLINE } from "@/resources/constants";
import { createMockEncounters } from "./mocking/patient-list-mocking";
import { PATIENT_LIST_VIEWS } from "./patient-views";

const { useResources } = encounterService;

const styles = {
  container: (theme: Theme) => css`
    width: 100vw;
    background-color: ${theme.palette.primary[50]};
    height: 100%;
  `,
  table: (theme: Theme) => css`
    border: none;
    border-radius: 0;
    height: 100%;

    .MuiDataGrid-columnHeaders {
      border: none;

      .MuiDataGrid-pinnedColumnHeaders {
        transition: box-shadow 0.2s ease-in-out;
        background-color: ${theme.palette.primary[50]};
        box-shadow: 8px 0px 16px 0px rgb(26 31 52 / 15%);
        border-right: 1px solid ${theme.palette.primary[100]};
        // Hack, add 1px padding to match width of body pinned columns
        padding-right: 1px;
      }

      .MuiDataGrid-columnHeaderTitle {
        font-size: 0.875rem;
        font-weight: 700;
        color: ${theme.palette.primary[700]};
      }
    }

    .MuiDataGrid-main {
      padding-left: 1.5rem;
    }

    .MuiDataGrid-cell {
      display: flex;
      align-items: center;
    }

    .MuiDataGrid-cell[data-field="actions"] {
      justify-content: center;
      padding: 0;
    }

    .MuiDataGrid-virtualScroller {
      ${scrollableMixin};
      overflow: overlay;
      border: 1px solid ${theme.palette.primary[100]};
      border-bottom: none;
      border-right: none;
      border-top-left-radius: 1rem;

      .MuiDataGrid-pinnedColumns--left {
        transition: box-shadow 0.2s ease-in-out;
        box-shadow: 8px 0px 16px 0px rgb(26 31 52 / 15%);
        border-right: 1px solid ${theme.palette.primary[100]};
        background-color: ${theme.palette.primary[25]};
      }

      .MuiDataGrid-virtualScrollerRenderZone {
        min-height: 100%;
        background-color: ${theme.palette.primary[25]};
      }

      .MuiDataGrid-pinnedColumns--left,
      .MuiDataGrid-virtualScrollerRenderZone {
        > :nth-of-type(even) {
          background-color: ${theme.palette.primary[50]};
        }
        > :nth-of-type(odd) {
          background-color: ${theme.palette.primary[25]};
        }
      }
    }

    .patient-list-row--distress {
      background-color: #ffedec !important;
    }

    .MuiDataGrid-footerContainer {
      border: none;
      min-height: unset;
    }

    &.hide-left-shadow {
      .MuiDataGrid-columnHeaders > .MuiDataGrid-pinnedColumnHeaders,
      .MuiDataGrid-virtualScroller .MuiDataGrid-pinnedColumns--left {
        box-shadow: none !important;
        border-right: none !important;
      }
    }
  `,
};

// Don't want to support more than 100 as it's too costly on the DB
const pageSizeOptions = [20, 50, 100];
const initialSort: GridSortModel = [{ field: "arrived", sort: "desc" }];
const initialPagination: Pagination = { page: 0, perPage: 100 };
const columns = patientListColumns;

const slots = {
  pagination: PatientPagination,
  toolbar: PatientToolbar,
} as any;

const getRowClass = (params: GridRowClassNameParams) => {
  const classPrefix = "patient-list-row";
  let classes = "";

  const evenRow = params.indexRelativeToCurrentPage % 2 === 0;
  classes += `${classPrefix}--${evenRow ? "even" : "odd"} `;

  if (params.isFirstVisible) {
    classes += `${classPrefix}--first `;
  }

  if (params.isLastVisible) {
    classes += `${classPrefix}--last `;
  }

  const encounter = params.row as PatientEncounter;
  if (encounter.distressed) {
    classes += `${classPrefix}--distress `;
  }

  return classes;
};

const usePaginationModel = ({
  setPagination,
  pagination,
}: {
  setPagination: (pagination: Pagination) => void;
  pagination: Pagination;
}) => {
  const onPageChange = React.useCallback((model: GridPaginationModel) => {
    setPagination({ page: model.page, perPage: model.pageSize });
  }, [setPagination]);

  const paginationModel = useMemo(() => ({
    page: pagination.page,
    pageSize: pagination.perPage,
  }), [pagination.page, pagination.perPage]);

  return { paginationModel, onPageChange };
};

const getStorage = (path?: string) => {
  const local = localStorage.getItem("trierptntlst");
  try {
    if (local) {
      if (path) return get(JSON.parse(local), path);
      else return JSON.parse(local);
    } else return;
  } catch (error) {
    return;
  }
};

const updateStorage = (key: string, value: object) => {
  const curValue = getStorage() || {};
  set(curValue, key, value);
  localStorage.setItem("trierptntlst", JSON.stringify(curValue));
};

// Custom hook to encapsulate data fetching and primary pagination logic
// Moved here to be defined before PatientListWithContextMenu
const usePatientListData = (filter: QueryParams, hookInitialPagination: Pagination, workOffline: boolean) => {
  // Only persist page size, not page number - always start at page 0
  const [savedPageSize, setSavedPageSize] = useLocalStorage<number>(
    "trier.home.pagination.pageSize",
    hookInitialPagination.perPage
  );

  // Always start at page 0, but use saved page size
  const savedPagination: Pagination = {
    page: 0,
    perPage: savedPageSize || hookInitialPagination.perPage
  };

  const setSavedPagination = (pagination: Pagination) => {
    setSavedPageSize(pagination.perPage);
  };

  const mockEncounters = useMemo(() => {
    if (workOffline) {
      return createMockEncounters(400);
    }
    return [];
  }, [workOffline]);

  const offlineSetPage = useCallback(
    () => {
      // For offline mode, we don't persist page changes since we always start at page 0
      // This is just for internal state management
    },
    []
  );

  const offlinePaginationObject = useMemo(() => ({
    pagination: savedPagination,
    setPagination: setSavedPagination,
    setPage: offlineSetPage,
  }), [savedPagination, setSavedPagination, offlineSetPage]);

  const offlineRefetch = useCallback(() => {
    console.warn("Offline refetch called");
  }, []);

  const offlineQuery = useMemo(() => ({
    isLoading: false,
    isFetching: false,
    refetch: offlineRefetch,
  }), [offlineRefetch]);

  // When offline, manually paginate the mock encounters
  const { page, perPage } = savedPagination;
  const paginatedMockEncounters = workOffline
    ? mockEncounters.slice(page * perPage, (page + 1) * perPage)
    : []; // Empty if not offline, will be overwritten by useResources

  const resourcesResult = workOffline ? {
    data: paginatedMockEncounters, // Use paginated data for offline mode
    total: mockEncounters.length, // Total is the full length of mock data
    query: offlineQuery,
    pagination: offlinePaginationObject,
  } : useResources({ filter, pagination: savedPagination });

  const { data: patients, total, query, pagination: resourcesPaginationData } = resourcesResult;

  const stableSetPage = useMemo(() => {
    return workOffline ? offlineSetPage : resourcesPaginationData?.setPage;
  }, [workOffline, offlineSetPage, resourcesPaginationData?.setPage]);

  const stableSetPagination = useMemo(() => {
    return (workOffline ? setSavedPagination : resourcesPaginationData?.setPagination) || (() => { console.warn("setPagination was not available for usePaginationModel"); });
  }, [workOffline, setSavedPagination, resourcesPaginationData?.setPagination]);

  const { paginationModel, onPageChange } = usePaginationModel({
    pagination: resourcesPaginationData?.pagination ?? savedPagination,
    setPagination: stableSetPagination,
  });

  return {
    patients,
    total,
    query,
    resourcesPagination: resourcesPaginationData,
    paginationModel,
    onPageChange,
    stableSetPage,
  };
};

interface PatientListCoreProps {
  patients: PatientEncounter[] | undefined;
  total: number | undefined;
  query: { isLoading: boolean; isFetching: boolean; refetch: (options?: { cancelRefetch?: boolean }) => void } | undefined;
  paginationModel: { page: number; pageSize: number };
  onPageChange: (model: GridPaginationModel) => void;
  stableSetPage: ((page: number) => void) | undefined;
  sort: { field: string, order: GridSortDirection } | undefined; // Matches useSortDataList output structure
  filters: ReturnType<typeof usePatientList>["filters"];
  advancedFilters: ReturnType<typeof usePatientList>["advancedFilters"];
  sortPropsSetSort: (sortModel: GridSortModel) => void;
}

const PatientListCore = ({
  patients,
  total,
  query,
  paginationModel,
  onPageChange,
  stableSetPage,
  sort: sortProp, // This will be { field, order: GridSortDirection }
  filters,
  advancedFilters,
  sortPropsSetSort
}: PatientListCoreProps) => {
  const navigate = useNavigate();
  const scrollLeft = useScrollLeft("MuiDataGrid-virtualScroller");
  const { apiRef, persistOrderedColNames, persistColWidths, persistPinnedColumns } = usePatientListApiRefCtx();
  const { onContextMenu } = useContext(PatientContext.context);

  const onPinnedColumnsChange = useCallback(
    (newPinned: { left?: string[]; right?: string[] }) => {
      persistPinnedColumns(newPinned);
    },
    [persistPinnedColumns]
  );

  const changeSort = React.useCallback((newSortModel: GridSortModel) => {
    let newSortValue = newSortModel;
    if (newSortModel && newSortModel.length === 0) {
      newSortValue = initialSort;
    }
    updateStorage("sort", newSortValue);
    sortPropsSetSort(newSortValue);
  }, [sortPropsSetSort]);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    stableSetPage?.(0);
    if (!WORK_OFFLINE) {
      setTimeout(() => { query?.refetch?.({ cancelRefetch: true }); }, 100);
    }
  }, [filters, advancedFilters]); // Removed stableSetPage to prevent loops

  useEffect(() => {
    if (firstRender.current) return;
    if (!WORK_OFFLINE) { query?.refetch?.(); }
  }, [sortProp]);

  const onSelect = React.useCallback((model: GridRowSelectionModel) => {
    if (model.length > 0) {
      const rowId = model[0] as string;
      const selectedPatient = patients?.find((r: PatientEncounter) => r.id === rowId);
      if (selectedPatient) { navigate(getPatientRoute(selectedPatient.id)); }
    }
  }, [navigate, patients]);

  const scrollClassNames = useMemo(() => scrollLeft === 0 ? "hide-left-shadow" : "", [scrollLeft]);
  const rowStyle = useMemo(() => ({ cursor: "pointer" }), []);
  const gridSlotProps = useMemo(() => ({ row: { onContextMenu, style: rowStyle } } as any), [onContextMenu, rowStyle]);

  const dataGridSortModel = useMemo(() => {
    if (!sortProp) return undefined;
    // sortProp is { field, order: GridSortDirection }
    // DataGridPro expects { field, sort: GridSortDirection }
    return [{ field: sortProp.field, sort: sortProp.order }] as GridSortModel;
  }, [sortProp]);

  console.log("[PatientListCore] Rendering with total:", total, "paginationModel:", paginationModel);

  return (
    <DataGridPro
      apiRef={apiRef}
      className={scrollClassNames}
      columns={columns}
      density="standard"
      // rowHeight={70}
      disableColumnFilter
      getRowClassName={getRowClass}
      getRowId={(row) => row.id}
      hideFooterSelectedRowCount
      initialState={{ pinnedColumns: defaultPinnedColumns }}
      loading={query?.isLoading || query?.isFetching}
      onPinnedColumnsChange={onPinnedColumnsChange}
      onColumnOrderChange={persistOrderedColNames}
      onColumnWidthChange={persistColWidths}
      onRowSelectionModelChange={onSelect}
      onSortModelChange={changeSort}
      pageSizeOptions={pageSizeOptions}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPageChange}
      rowCount={total || 0}
      rows={patients || []}
      slotProps={gridSlotProps}
      slots={slots}
      sortModel={dataGridSortModel}
      sx={styles.table as any}
      {...({} as any)}
    />
  );
};

const PatientListWithContextMenu = () => {
  const { sort: sortFromHook, sortProps } = useSortDataList(getStorage("sort") || initialSort);
  const { filters, advancedFilters, activeView } = usePatientList();
  const { settings } = useSettingsContext();
  const locations = ((settings as any)?.locations ?? []) as any[];

  const listDataFilter = useMemo(() => {
    let queryParamsSort: { field: string, order: "asc" | "desc" } | undefined = undefined;
    if (sortFromHook && (sortFromHook.order === "asc" || sortFromHook.order === "desc")) {
      // Map frontend field names to backend field names
      // arrivalTime column should sort by 'arrived' field on the backend
      const fieldMap: Record<string, string> = {
        arrivalTime: "arrived",
        seniorDoctor: "practitioner_snrDoctor_shortName",
        doctor: "practitioner_doctor_shortName",
        nurse: "practitioner_nurse_shortName",
      };
      const backendField = fieldMap[sortFromHook.field] || sortFromHook.field;
      
      // Ensure it's strictly "asc" or "desc" for QueryParams
      queryParamsSort = { field: backendField, order: sortFromHook.order };
    }

    const activeViewConfig = activeView
      ? PATIENT_LIST_VIEWS.find((view) => view.name === activeView)
      : undefined;

    if (activeViewConfig) {
      const params: QueryParams = {
        ...(activeViewConfig.query || {}),
      };

      if (queryParamsSort) {
        params.sort = queryParamsSort;
      }

      return params;
    }

    const status = filters?.status.length > 0 ? filters.status : stringify({ $ne: "discharged" });

    const params = { sort: queryParamsSort, status } as QueryParams;
    if (filters?.area.length > 0) {
      const locationShortNames = getLocationsByAreas(locations, filters.area);
      if (locationShortNames.length > 0) params["location.shortName"] = locationShortNames.join(",");
    }
    if (advancedFilters.snrDoctor?.length > 0) params.practitioners_snrDoctor = advancedFilters.snrDoctor;
    if (advancedFilters.doctor?.length > 0) params.practitioners_doctor = advancedFilters.doctor;
    if (advancedFilters.nurse?.length > 0) params.practitioners_nurse = advancedFilters.nurse;
    if (advancedFilters.ageRange?.length > 0) {
      const [start, end] = advancedFilters.ageRange.split("-");
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - parseInt(start);
      const endYear = currentYear - parseInt(end);
      params.patient_dob = [stringify({ $r: `${new Date(String(endYear)).getTime()},${new Date(String(startYear)).getTime()}` })];
    }
    return params;
  }, [sortFromHook, filters, advancedFilters, locations, activeView]);

  const {
    patients,
    total,
    query,
    paginationModel,
    onPageChange,
    stableSetPage,
  } = usePatientListData(listDataFilter, initialPagination, WORK_OFFLINE);

  const contextMenuSelector = React.useCallback(
    (event: React.MouseEvent) => {
      const id = event.currentTarget.getAttribute("data-id");
      const row = patients?.find((r: PatientEncounter) => r.id === id) as PatientEncounter;
      return row;
    },
    [patients]
  );
  const { value: selectedRow, options } = useContextMenuValue<PatientEncounter>(contextMenuSelector);

  return (
    <Box css={styles.container} sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", height: "100%" }}>
      <PatientContext.Provider options={options}>
        <PatientListCore
          patients={patients}
          total={total}
          query={query}
          paginationModel={paginationModel}
          onPageChange={onPageChange}
          stableSetPage={stableSetPage}
          sort={sortFromHook}
          filters={filters}
          advancedFilters={advancedFilters}
          sortPropsSetSort={sortProps.setSort}
        />
        {selectedRow ? <PatientContextMenu encounter={selectedRow} /> : null}
      </PatientContext.Provider>
    </Box>
  );
};

export const PatientList = () => (
  <WithPatientListApiRefCtx>
    <PatientListWithContextMenu />
  </WithPatientListApiRefCtx>
);

export default PatientList;
